import dayjs, {Dayjs, OpUnitType} from 'dayjs';

import {Asset, fetchAssetByUrn} from '@deviceinsight/ng-ui-api-client';
import {nanoid10} from '@deviceinsight/ng-ui-components';

import {
	AggregationQuerySettings,
	AggregationSettings,
	AssetToKpiMap,
	AssetToKpiPromiseMap,
	DateRange,
	KpiWidgetItem,
	SingleValueChartData
} from './types';
import {KpiIdentifier, KpiValueDescriptor} from './hooks/useMultipleKpiValues';
import {StyleApi} from '@deviceinsight/ng-ui-scale-lib/api';
import {nonNull} from '../../../../utils/tsUtils';
import {
	DataProcessingDefinition,
	DataProcessingDefinitionProcessingPeriod,
	fetchDataProcessingDefinitionsAssignedToAsset
} from '../../../../api/dataProcessingDefinitionApi';
import {getKpiGeneric, getKpiGenericWithTimestamp, KpiParams} from '../../../../api/kpiApi';

// @ts-ignore
export const MomentStartOf: Record<string, OpUnitType> = Object.freeze({
	HOUR: 'hour',
	DAY: 'day',
	ISO_WEEK: 'isoWeek',
	// isoWeek starts on monday, week starts on sunday
	MONTH: 'month'
});

export const MomentTimeUnit: Record<string, OpUnitType> = Object.freeze({
	MINUTE: 'minute',
	HOUR: 'hour',
	DAY: 'day',
	WEEK: 'week',
	MONTH: 'month'
});

export const DIGITAL_CLOCK_PATTERN = /^(([0-1][0-9])|2[0-3]):[0-5][0-9]$/;

export const DateFormats = Object.freeze({
	TIME: 'HH:mm',
	DATE: 'YYYY-MM-DD',
	YEAR_MONTH: 'YYYY-MMMM',
	DATE_TIME: 'YYYY-MM-DDTHH:mm:ss[Z]',
	DATE_TIME_DISPLAY: 'DD.MM.YYYY HH:mm'
});

export const KpiGroupBy = Object.freeze({
	HOUR: 'BY_HOUR',
	DAY: 'BY_DAY',
	WEEK: 'BY_WEEK',
	MONTH: 'BY_MONTH'
});

export const ChartSeriesType = Object.freeze({
	COLUMN: 'column',
	LINE: 'line',
	AREA: 'area'
});

export const KpiQuery = Object.freeze({
	SUM: 'QUERY_SUM',
	AVERAGE: 'QUERY_AVERAGE',
	MAX: 'QUERY_MAX',
	MIN: 'QUERY_MIN',
	ALL: 'QUERY_ALL'
});

export type KpiTimeframeType = 'CURRENT' | 'LAST' | 'PAST' | 'CUSTOM';
export type KpiPeriodTypeType = keyof typeof KpiGroupBy;
export type KpiGroupByValuesType = (typeof KpiGroupBy)[keyof typeof KpiGroupBy];
export type WorkingShiftDay = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/* update interval for the  single period charts if no values have been sent */
export const UPDATE_WIDGET_INTERVAL = 10 * 60 * 1000; // 10 minutes

const assetToKpiCache: AssetToKpiMap = {};
const assetToKpiPromiseCache: AssetToKpiPromiseMap = {};
const assetUrnToAssetIdCache: Record<string, number> = {};
const assetUrnToAssetIdPromiseCache: Record<string, Promise<number>> = {};

export const checkBoxMessages = {
	title: {
		id: 'dashboard.widgets.kpiaggredatedchart.stacked.title',
		defaultValue: 'If this is selected, the columns in the chart get stacked'
	},
	label: {
		id: 'dashboard.widgets.kpiaggredatedchart.stacked.label',
		defaultValue: 'Stacked chart'
	}
};

export const KPI_ERROR_NOT_UNIQUE = '[kpiNotUnique]';
const KPI_ERROR_NOT_AVAILABLE = '[kpiNotAvailable]';
export const PERIOD_TIMEFRAME_DEFAULT = 'CURRENT';
export const PERIOD_TYPE_DEFAULT = 'DAY';
export const PERIOD_COUNT_DEFAULT = 1;

const alignToProcessingPeriod = (
	startUnaligned: Dayjs,
	endUnaligned: Dayjs,
	processingPeriod: DataProcessingDefinitionProcessingPeriod
): DateRange => {
	const start = dayjs(startUnaligned).startOf('minute');
	const end = dayjs(endUnaligned).startOf('minute');

	switch (processingPeriod) {
		case 'INSTANT':
			return {
				start: startUnaligned,
				end: endUnaligned
			};

		case 'MIN1':
			return {
				start,
				end
			};

		case 'MIN15': {
			const remainder = start.minute() % 15;
			start.subtract(remainder, 'minutes');
			end.subtract(remainder, 'minutes');
			return {
				start,
				end
			};
		}

		case 'MIN30': {
			const remainder = start.minute() % 30;
			start.subtract(remainder, 'minutes');
			end.subtract(remainder, 'minutes');
			return {
				start,
				end
			};
		}

		case 'HOUR': {
			start.startOf('hour');
			end.startOf('hour');
			return {
				start,
				end
			};
		}

		default: {
			const errorMessage = `alignToProcessingPeriod: invalid processingPeriod: ${processingPeriod}`;
			throw new Error(errorMessage);
		}
	}
};

export const getDateRange = (
	startingFrom: Dayjs,
	query: AggregationQuerySettings,
	processingPeriod?: DataProcessingDefinitionProcessingPeriod | null
): DateRange => {
	if (query.periodTimeframe === 'CURRENT' && query.periodType) {
		const {momentTimeUnit, momentStartOf} = getGroupBySettingsForPeriodType(query.periodType);
		const startingFromAligned = dayjs(startingFrom).startOf(momentStartOf);
		const start = dayjs(startingFromAligned);
		const end = dayjs(start).add(1, momentTimeUnit);
		return processingPeriod
			? alignToProcessingPeriod(start, end, processingPeriod)
			: {
					start,
					end
			  };
	} else if (query.periodTimeframe === 'LAST' && query.periodType) {
		const {momentTimeUnit, momentStartOf} = getGroupBySettingsForPeriodType(query.periodType);
		const startingFromAligned = dayjs(startingFrom).startOf(momentStartOf);
		const start = dayjs(startingFromAligned).subtract(query.periodCount - 1, momentTimeUnit);
		const end = dayjs(startingFromAligned).add(1, momentTimeUnit);
		return processingPeriod
			? alignToProcessingPeriod(start, end, processingPeriod)
			: {
					start,
					end
			  };
	} else if (query.periodTimeframe === 'PAST' && query.periodType) {
		const {momentTimeUnit, momentStartOf} = getGroupBySettingsForPeriodType(query.periodType);
		const startingFromAligned = dayjs(startingFrom).startOf(momentStartOf);
		const start = dayjs(startingFromAligned).subtract(query.periodCount, momentTimeUnit);
		const end =
			query.periodType === 'DAY'
				? start.endOf('day')
				: dayjs(startingFromAligned).add(1, momentTimeUnit).subtract(query.periodCount, momentTimeUnit);
		return processingPeriod
			? alignToProcessingPeriod(start, end, processingPeriod)
			: {
					start,
					end
			  };
	} else if (query.periodTimeframe === 'CUSTOM') {
		return {
			start: dayjs(query.customPeriodFrom),
			end: dayjs(query.customPeriodTo)
		};
	} else {
		throw new Error(
			`getDateRange: unknown periodTimeframe/periodType combination: ${query.periodTimeframe} ${
				query.periodType || '<empty>'
			}`
		);
	}
};

export const getUniqueKpisForAsset = (assetId: number): Promise<Array<DataProcessingDefinition>> => {
	return fetchDataProcessingDefinitionsAssignedToAsset(assetId).then(
		(definitions: Array<DataProcessingDefinition>) => {
			// Exclude calculated datapoints
			const kpis = definitions.filter((def) => !def.definition.publishAsDatapointValues);

			const seen = new Set(kpis.map(({definition: {id}}) => id));
			if (seen.size < kpis.length) {
				// Exclude duplicates
				return [...seen].map((id) => kpis.find((kpi) => id === kpi.definition.id)).filter(nonNull);
			} else {
				return kpis;
			}
		}
	);
};

const getKpisByNameForAsset = (assetId: number, kpiName: string): Promise<Array<DataProcessingDefinition>> => {
	return getUniqueKpisForAsset(assetId).then((kpis: Array<DataProcessingDefinition>) => {
		return kpis.filter((assetKpi) => assetKpi.definition.kpiName === kpiName);
	});
};

const getKpi = (assetId: number, kpiName: string): Promise<DataProcessingDefinition> => {
	const assetToKpi = assetToKpiCache[assetId];

	if (assetToKpi && assetToKpi[kpiName]) {
		return Promise.resolve(assetToKpi[kpiName]);
	} else {
		const assetToKpiPromise = assetToKpiPromiseCache[assetId];

		// @ts-ignore
		if (assetToKpiPromise && assetToKpiPromise[kpiName]) {
			return assetToKpiPromise[kpiName];
		} else {
			const promise = getKpisByNameForAsset(assetId, kpiName).then((filteredKpis) => {
				if (filteredKpis.length === 0) {
					throw new Error(
						KPI_ERROR_NOT_AVAILABLE + ' - The kpi configured with this widget is not available'
					);
				} else if (filteredKpis.length > 1) {
					throw new Error(KPI_ERROR_NOT_UNIQUE + ' - The kpi configured with this widget is not available');
				}

				const kpi = filteredKpis[0];

				if (kpi) {
					if (!assetToKpiCache[assetId]) {
						assetToKpiCache[assetId] = {};
					}

					assetToKpiCache[assetId][kpiName] = kpi;
					delete assetToKpiPromiseCache[assetId][kpiName];
					return Promise.resolve(kpi);
				} else {
					throw new Error(
						KPI_ERROR_NOT_AVAILABLE + ' - The kpi configured with this widget is not available'
					);
				}
			});

			if (!assetToKpiPromiseCache[assetId]) {
				assetToKpiPromiseCache[assetId] = {};
			}

			assetToKpiPromiseCache[assetId][kpiName] = promise;
			return promise;
		}
	}
};

const getAssetIdByAssetUrn = (assetUrn: string): Promise<number> => {
	if (assetUrnToAssetIdCache[assetUrn]) {
		return Promise.resolve(assetUrnToAssetIdCache[assetUrn]);
	} else {
		// @ts-ignore
		if (assetUrnToAssetIdPromiseCache[assetUrn]) {
			return assetUrnToAssetIdPromiseCache[assetUrn];
		} else {
			const promise = fetchAssetByUrn(assetUrn).then((asset) => {
				assetUrnToAssetIdCache[assetUrn] = asset.id;
				delete assetUrnToAssetIdPromiseCache[assetUrn];
				return asset.id;
			});
			assetUrnToAssetIdPromiseCache[assetUrn] = promise;
			return promise;
		}
	}
};

type GroupBySettings = {
	dateFormat: string;
	momentTimeUnit: (typeof MomentTimeUnit)[keyof typeof MomentTimeUnit];
	momentStartOf: OpUnitType;
};
export const getGroupBySettingsForPeriodType = (periodType: KpiPeriodTypeType | null | undefined): GroupBySettings => {
	if (periodType) {
		return getGroupBySettings(KpiGroupBy[periodType]);
	} else {
		throw new Error('getGroupBySettingsForPeriodType: periodType should not be null!');
	}
};
export const getGroupBySettings = (groupBy: KpiGroupByValuesType): GroupBySettings => {
	switch (groupBy) {
		case KpiGroupBy.HOUR:
			return {
				dateFormat: DateFormats.DATE_TIME_DISPLAY,
				momentTimeUnit: MomentTimeUnit.HOUR,
				momentStartOf: MomentStartOf.HOUR
			};

		case KpiGroupBy.DAY:
			return {
				dateFormat: 'DD.MM',
				momentTimeUnit: MomentTimeUnit.DAY,
				momentStartOf: MomentStartOf.DAY
			};

		case KpiGroupBy.WEEK:
			return {
				dateFormat: 'W',
				momentTimeUnit: MomentTimeUnit.WEEK,
				momentStartOf: MomentStartOf.ISO_WEEK
			};

		case KpiGroupBy.MONTH:
			return {
				dateFormat: 'MMM',
				momentTimeUnit: MomentTimeUnit.MONTH,
				momentStartOf: MomentStartOf.MONTH
			};

		default:
			throw new Error(`getGroupBySettings: unknown groupBy value: ${groupBy || '<empty>'}`);
	}
};

const composeKpiParams = (
	kpiName: string,
	kpiUuid: string | null | undefined,
	fromPointOfTime: Dayjs,
	processingPeriod: DataProcessingDefinitionProcessingPeriod,
	settings: AggregationSettings,
	userId: number,
	assetId: number
): KpiParams => {
	if (!kpiUuid) {
		throw new Error(`composeKpiParams: empty kpiUUid: ${assetId} - ${kpiName}`);
	}

	const {aggregationType} = settings;
	const timeParams = getTimeParams(settings, fromPointOfTime, processingPeriod);
	const params: KpiParams = {
		kpiName,
		externalUuid: kpiUuid,
		queryAssetsBy: 'BY_ASSET_ID',
		queryAssetsById: assetId,
		queryType: aggregationType,
		...timeParams,
		userId
	};
	if (settings.groupBy) params.groupBy = settings.groupBy;
	return params;
};

const getTimeParams = (
	settings: AggregationSettings,
	fromPointOfTime: Dayjs,
	processingPeriod: DataProcessingDefinitionProcessingPeriod
) => {
	const {start, end} = getDateRange(fromPointOfTime, settings.query, processingPeriod);
	const {workingShiftEnabled, workingShiftDays} = settings.query;
	const workingShiftTimeFrom = settings.query.workingShiftTimeFrom ?? start.format(DateFormats.TIME);
	const workingShiftTimeTo = settings.query.workingShiftTimeTo ?? end.format(DateFormats.TIME);

	if (
		((canAddShift(settings.query.periodTimeframe, settings.query.periodType) && workingShiftEnabled) ||
			settings.query.periodType === 'HOUR') &&
		!!workingShiftDays &&
		!!workingShiftTimeFrom &&
		!!workingShiftTimeTo
	) {
		return {
			dateFrom: start.format(DateFormats.DATE),
			dateTo: end.format(DateFormats.DATE),
			daysOfWeek: workingShiftDays.join(','),
			timeFrom: workingShiftTimeFrom,
			timeTo: workingShiftTimeTo
		};
	} else if (
		settings.query.periodTimeframe === 'CUSTOM' ||
		settings.query.periodTimeframe === 'LAST' ||
		settings.query.periodTimeframe === 'PAST'
	) {
		return {
			dateFrom: start.format(DateFormats.DATE),
			dateTo: end.format(DateFormats.DATE),
			timeFrom: start.format(DateFormats.TIME),
			timeTo: end.format(DateFormats.TIME)
		};
	} else {
		return {
			dateFrom: start.format(DateFormats.DATE),
			dateTo: end.format(DateFormats.DATE)
		};
	}
};

export const canAddShift = (
	periodTimeframe: KpiTimeframeType,
	periodType: KpiPeriodTypeType | null | undefined
): boolean => {
	return periodTimeframe === 'CUSTOM' || periodType === 'WEEK' || periodType === 'DAY' || periodType === 'MONTH';
};
export const createKpiChartItem = (
	asset: Asset | null | undefined,
	kpiName: string | null | undefined,
	color: string | null | undefined
): KpiWidgetItem => {
	return {
		kpiName,
		uuid: `kpiName_${nanoid10()}`,
		assetUrn: asset ? asset.urn : undefined,
		color: color || StyleApi.getNextColor(),
		label: '',
		unit: '',
		chartType: ChartSeriesType.COLUMN
	};
};
export const createFooterLabel = (
	aggregationSettings: AggregationSettings,
	dashboardTime: number | null | undefined
): string | null | undefined => {
	if (dashboardTime) {
		const {start, end} = getDateRange(dayjs(dashboardTime), aggregationSettings.query);
		return `${start.format(DateFormats.DATE_TIME_DISPLAY)} - ${end.format(DateFormats.DATE_TIME_DISPLAY)}`;
	} else {
		const {start, end} = getDateRange(dayjs(), aggregationSettings.query);
		return `${start.format(DateFormats.DATE_TIME_DISPLAY)} - ${end.format(DateFormats.DATE_TIME_DISPLAY)}`;
	}
};
export const fetchAssetIdAndKpi = async ({
	assetUrn,
	kpiName
}: KpiIdentifier): Promise<{
	assetId: number;
	kpi: DataProcessingDefinition;
}> => {
	const assetId = await getAssetIdByAssetUrn(assetUrn);
	const kpi = await getKpi(assetId, kpiName);
	return {
		assetId,
		kpi
	};
};
export const fetchAggregatedFacts = async (
	{assetUrn, kpiName}: KpiIdentifier,
	userId: number,
	time: number,
	settings: AggregationSettings
): Promise<KpiValueDescriptor> => {
	const {assetId, kpi} = await fetchAssetIdAndKpi({
		assetUrn,
		kpiName
	});
	const kpiUuid = kpi.definition.id;
	const processingPeriod = kpi.definition.processingPeriod;
	const params = composeKpiParams(kpiName, kpiUuid, dayjs(time), processingPeriod, settings, userId, assetId);
	const {aggregatedFacts} = await getKpiGenericWithTimestamp(params);
	return {
		assetUrn,
		kpiName,
		facts: aggregatedFacts || []
	};
};
export const fetchKpiValue = async (
	{assetUrn, kpiName}: KpiIdentifier,
	userId: number,
	time: number,
	settings: AggregationSettings
): Promise<KpiValueDescriptor> => {
	const {assetId, kpi} = await fetchAssetIdAndKpi({
		assetUrn,
		kpiName
	});
	const kpiUuid = kpi.definition.id;
	const processingPeriod = kpi.definition.processingPeriod;
	const params = composeKpiParams(kpiName, kpiUuid, dayjs(time), processingPeriod, settings, userId, assetId);
	const result: Array<number> = await getKpiGeneric(params);
	const value = result && result.length > 0 && result[0] != null ? result[0] : NaN;
	return {
		assetUrn,
		kpiName,
		value
	};
};
export const createChartData = (
	items: KpiWidgetItem[],
	values: KpiValueDescriptor[],
	contextAssetUrn: string | null | undefined
): SingleValueChartData[] =>
	items
		.map(({assetUrn: itemAssetUrn, kpiName, ...item}) => {
			const assetUrn = contextAssetUrn || itemAssetUrn;

			if (!assetUrn || !kpiName) {
				return null;
			}

			const valueDescriptor = values.find(
				(valueDescriptor) => valueDescriptor.assetUrn === assetUrn && valueDescriptor.kpiName === kpiName
			);
			return {...item, assetUrn, kpiName, value: valueDescriptor?.value};
		})
		.filter(nonNull);
export const updatedKpiItem = (
	items: KpiWidgetItem[],
	index: number,
	data: {
		assetUrn?: string | null | undefined;
		kpiName?: string | null | undefined;
	}
): KpiWidgetItem[] => [...items.slice(0, index), {...items[index], ...data}, ...items.slice(index + 1)];
export const isKpiWidgetItemValid = (item: KpiWidgetItem): boolean => {
	const {assetUrn, kpiName, chartType} = item;
	return !!assetUrn && !!kpiName && !!chartType;
};
export const validateKpiWidgetTimeConfig = (
	periodTimeframe: KpiTimeframeType,
	periodType: KpiPeriodTypeType | null | undefined,
	workingShiftEnabled: boolean,
	workingShiftDays: WorkingShiftDay[] | null | undefined,
	workingShiftTimeFrom: string | null | undefined,
	workingShiftTimeTo: string | null | undefined
): boolean => {
	if (canAddShift(periodTimeframe, periodType) && workingShiftEnabled) {
		return (
			!!workingShiftDays &&
			(periodType === 'DAY' || workingShiftDays.length > 0) &&
			!!workingShiftTimeFrom &&
			!!workingShiftTimeTo
		);
	} else {
		return true;
	}
};
